// Step 1 の進行・完了・失敗を監視する画面
import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Home, Loader2, RotateCcw, Sparkles } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/create/template/$id/step1-processing")({
  head: () => ({
    meta: [
      { title: "動画を生成中 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Step1ProcessingPage,
});

type Row = {
  user_id: string;
  template_state: string | null;
  intermediate_image_url: string | null;
  failure_reason: string | null;
  error_message: string | null;
};

const FAILURE_MESSAGES: Record<string, string> = {
  content_blocked:
    "不適切なコンテンツが検出されました。別の素材でお試しください。",
  gateway_unavailable:
    "AI サービスが一時的に利用できません。時間を置いてお試しください。",
  generation_failed:
    "動画生成に失敗しました。お手数ですが再試行をお願いします。",
};

const STEPS = [
  { key: "analyzing", label: "素材を解析中" },
  { key: "planning", label: "AI が変身を考えてます" },
  { key: "rendering", label: "動画を生成中" },
] as const;

function Step1ProcessingPage() {
  const { id } = useParams({ from: "/_authenticated/create/template/$id/step1-processing" });
  const navigate = useNavigate();
  const [row, setRow] = useState<Row | null>(null);
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  const navigatedRef = useRef(false);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    let active = true;
    const fetchOnce = async () => {
      const { data } = await supabase
        .from("generations")
        .select("user_id, template_state, intermediate_image_url, failure_reason, error_message")
        .eq("id", id)
        .maybeSingle();
      if (!active || !data) return;
      setRow(data as Row);
    };
    void fetchOnce();
    const t = window.setInterval(fetchOnce, 3000);
    return () => {
      active = false;
      window.clearInterval(t);
    };
  }, [id]);

  const state = row?.template_state ?? "step1_processing";
  const isFailed = state === "step1_failed";
  const isReady = state === "step1_complete" && !!row?.intermediate_image_url;

  useEffect(() => {
    if (!isReady || navigatedRef.current) return;
    navigatedRef.current = true;
    navigate({ to: "/create/template/$id/preview", params: { id } });
  }, [isReady, id, navigate]);

  const elapsed = Math.floor((now - startedAt) / 1000);
  // 30秒前後で完了することを想定
  const stepIndex = Math.min(STEPS.length - 1, Math.floor(elapsed / 12));
  const remaining = Math.max(0, 45 - elapsed);

  if (isFailed) {
    const reason = row?.failure_reason ?? "generation_failed";
    const message =
      FAILURE_MESSAGES[reason] ?? row?.error_message ?? "予期せぬエラーが発生しました。";
    const detail =
      row?.error_message && row.error_message !== message ? row.error_message : null;
    return (
      <main className="min-h-screen bg-background px-4 py-10">
        <div className="mx-auto max-w-xl">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <h1 className="mb-2 text-xl font-bold">動画の生成に失敗しました</h1>
            <p className="mb-2 text-sm text-muted-foreground">{message}</p>
            {detail && (
              <p className="mx-auto mb-3 max-w-sm break-words rounded-md border border-destructive/20 bg-destructive/5 px-2 py-1 text-[11px] text-destructive/80">
                詳細: {detail}
              </p>
            )}
            <p className="mb-6 inline-block rounded-md bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              ✓ クレジットは返却されました
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button asChild>
                <Link to="/create/template">
                  <RotateCcw className="mr-1 h-4 w-4" />
                  もう一度試す
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/dashboard">
                  <Home className="mr-1 h-4 w-4" />
                  ダッシュボードに戻る
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-xl">
        <header className="mb-6 text-center">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            生成中
          </div>
          <h1 className="text-2xl font-bold sm:text-3xl">動画を生成中...</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            画面を閉じても処理は続行されます。完了したら自動で結果画面へ移動します。
          </p>
        </header>

        <Card className="p-8">
          <div className="mb-6 flex justify-center">
            <Loader2 className="h-14 w-14 animate-spin text-primary" />
          </div>

          <ul className="mb-6 space-y-3">
            {STEPS.map((s, i) => {
              const done = i < stepIndex;
              const active = i === stepIndex;
              return (
                <li key={s.key} className="flex items-center gap-2 text-sm">
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : active ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : (
                    <span className="inline-block h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                  )}
                  <span
                    className={cn(
                      done
                        ? "text-foreground"
                        : active
                          ? "font-semibold text-foreground"
                          : "text-muted-foreground",
                    )}
                  >
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ul>

          <p className="text-center text-xs text-muted-foreground">
            {remaining > 0 ? `あと約 ${remaining} 秒` : "まもなく完了します"}
          </p>
        </Card>
      </div>
    </main>
  );
}
